/**
 * CreateLeadModal.tsx
 * Modal para crear un lead manualmente sin conversación previa
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Save,
  User,
  Phone,
  Mail,
  DollarSign,
  Home,
  MapPin,
  FileText,
  Loader
} from 'lucide-react'
import api from '@/services/api'
import { toast } from 'react-toastify'
import { COUNTRY_CODE_OPTIONS, DEFAULT_COUNTRY_CODE } from '@/constants/countryCodes'

interface CreateLeadModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

const PROPERTY_TYPES = [
  { value: '', label: 'Seleccionar...' },
  { value: 'casa', label: 'Casa' },
  { value: 'departamento', label: 'Departamento' },
  { value: 'terreno', label: 'Terreno' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'oficina', label: 'Oficina' },
  { value: 'bodega', label: 'Bodega' }
]

const PURPOSE_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'compra', label: 'Compra' },
  { value: 'renta', label: 'Renta' },
  { value: 'inversion', label: 'Inversión' },
  { value: 'leasing', label: 'Leasing' }
]

const CURRENCY_OPTIONS = [
  { value: 'MXN', label: 'MXN' },
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' }
]

const TEMPERATURE_OPTIONS = [
  { value: 'hot', label: '🔥 Caliente', color: 'text-red-400' },
  { value: 'warm', label: '☀️ Tibio', color: 'text-yellow-400' },
  { value: 'cold', label: '❄️ Frío', color: 'text-blue-400' }
]

const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Captura Manual' },
  { value: 'referido', label: 'Referido' },
  { value: 'llamada', label: 'Llamada Entrante' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'portal', label: 'Portal Inmobiliario' },
  { value: 'espectacular', label: 'Espectacular/Lona' },
  { value: 'otro', label: 'Otro' }
]

const CreateLeadModal: React.FC<CreateLeadModalProps> = ({ isOpen, onClose, onSave }) => {
  const [saving, setSaving] = useState(false)
  const [countryCode, setCountryCode] = useState('+52')
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    budget_min: '',
    budget_max: '',
    budget_currency: 'MXN',
    property_type: '',
    zones: '',
    purpose: '',
    temperature: 'warm',
    source: 'manual',
    notes: ''
  })

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      budget_min: '',
      budget_max: '',
      budget_currency: 'MXN',
      property_type: '',
      zones: '',
      purpose: '',
      temperature: 'warm',
      source: 'manual',
      notes: ''
    })
    setCountryCode('+52')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return 'El nombre es requerido'
    }
    if (!formData.phone.trim()) {
      return 'El teléfono es requerido'
    }
    
    // Validar dígitos según país
    const selectedCountry = COUNTRY_CODE_OPTIONS.find(c => c.value === countryCode)
    const digitsOnly = formData.phone.replace(/\D/g, '')
    if (selectedCountry && digitsOnly.length !== selectedCountry.digits) {
      return `El teléfono para ${selectedCountry.country} debe tener ${selectedCountry.digits} dígitos`
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Formato de email inválido'
    }

    if (formData.budget_min && formData.budget_max) {
      if (parseFloat(formData.budget_min) > parseFloat(formData.budget_max)) {
        return 'El presupuesto mínimo no puede ser mayor al máximo'
      }
    }

    return null
  }

  const handleSave = async () => {
    const error = validateForm()
    if (error) {
      toast.warning(error as string)
      return
    }

    setSaving(true)
    try {
      const fullPhone = `${countryCode}${formData.phone.replace(/\D/g, '')}`
      
      const payload = {
        name: formData.name.trim(),
        phone: fullPhone,
        email: formData.email.trim() || null,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        budget_currency: formData.budget_currency,
        property_type: formData.property_type || null,
        zones: formData.zones ? formData.zones.split(',').map(z => z.trim()).filter(Boolean) : [],
        purpose: formData.purpose || null,
        temperature: formData.temperature,
        source: formData.source,
        notes: formData.notes.trim() || null
      }

      await api.post('/api/agents/leads/create-manual', payload)
      toast.success('Lead creado exitosamente')
      resetForm()
      onSave()
      onClose()
    } catch (error: any) {
      console.error('Error creating lead:', error)
      const errorMsg = error.response?.data?.error || 'Error al crear el lead'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 rounded-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Nuevo Lead</h2>
                <p className="text-gray-400 text-xs sm:text-sm">Captura manual de prospecto</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
            
            {/* Sección: Datos de Contacto */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <User className="w-4 h-4" /> Datos de Contacto
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Nombre */}
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">
                    Nombre <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre completo del prospecto"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2.5 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Teléfono */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">
                    Teléfono <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-2.5 text-white text-sm focus:border-blue-500 w-24"
                    >
                      {COUNTRY_CODE_OPTIONS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="10 dígitos"
                      className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 sm:px-4 py-2.5 text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Sección: Origen y Temperatura */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Clasificación
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Origen */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Origen</label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  >
                    {SOURCE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Temperatura */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Temperatura</label>
                  <select
                    value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  >
                    {TEMPERATURE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Sección: Presupuesto */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Presupuesto
              </h3>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Mínimo</label>
                  <input
                    type="number"
                    value={formData.budget_min}
                    onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                    placeholder="500,000"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Máximo</label>
                  <input
                    type="number"
                    value={formData.budget_max}
                    onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                    placeholder="2,000,000"
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Moneda</label>
                  <select
                    value={formData.budget_currency}
                    onChange={(e) => setFormData({ ...formData, budget_currency: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  >
                    {CURRENCY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Sección: Preferencias */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <Home className="w-4 h-4" /> Preferencias
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Tipo de Propiedad</label>
                  <select
                    value={formData.property_type}
                    onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  >
                    {PROPERTY_TYPES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Propósito</label>
                  <select
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                  >
                    {PURPOSE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Zonas de Interés
                </label>
                <input
                  type="text"
                  value={formData.zones}
                  onChange={(e) => setFormData({ ...formData, zones: e.target.value })}
                  placeholder="Ej: Campestre, Centro, Zona Dorada (separadas por coma)"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500"
                />
              </div>
            </div>

            {/* Sección: Notas */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Notas
              </h3>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Información adicional sobre el prospecto..."
                rows={3}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:border-blue-500 resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-white/10 bg-slate-800/50">
            <button
              onClick={handleClose}
              className="px-4 py-2.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg transition-all text-sm font-medium disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Crear Lead
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default CreateLeadModal
